/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {Popover} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useState, type Dispatch, type SetStateAction} from "react";
import {ArrowDownUp} from "react-bootstrap-icons";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {resolveSortActiveRuleCount} from "~/components/ottopilotDataViewer/dataViewerSortDefaults";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import AddIcon from "@atlaskit/icon/core/add";
import CrossIcon from "@atlaskit/icon/core/cross";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import DragHandleVerticalIcon from "@atlaskit/icon/core/drag-handle-vertical";
import SearchIcon from "@atlaskit/icon/core/search";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeDropdownItemCheckbox} from "@vesta/ananke/components/anankeDropdownItemCheckbox";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import type {OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {AnankeBadge} from "@vesta/ananke/components/anankeBadge";
import {BottomDrawer} from "~/components/BottomDrawer";
import {token} from "@atlaskit/tokens";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

/** The sort control only needs column metadata and sorting state; admin pages can reuse it too. */
export type SortComponentController<T extends {id: Uuid}> = Pick<OttopilotDataViewerController<T>,
    "columns" | "sortConfiguration" | "defaultSortConfiguration" | "showDefaultSortAsActive" | "setSortConfiguration"
>;

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

type EphemeralSortLineItem = SortConfiguration["lineItems"][number] & {
    lineItemId: Uuid;
};

type EphemeralSortConfiguration = {
    revisionId: Uuid;
    lineItems: Array<EphemeralSortLineItem>;
};

function createEphemeralSortLineItem(
    columnId = "",
    direction = "" as SortDirection,
): EphemeralSortLineItem {
    return {
        lineItemId: generateUuid(),
        columnId,
        direction,
    };
}

function openEphemeralSortConfiguration(sortConfiguration: SortConfiguration): EphemeralSortConfiguration {
    return {
        revisionId: sortConfiguration.revisionId,
        lineItems:
            sortConfiguration.lineItems.length > 0
                ? sortConfiguration.lineItems.map((lineItem) =>
                      createEphemeralSortLineItem(lineItem.columnId, lineItem.direction),
                  )
                : [createEphemeralSortLineItem()],
    };
}

function appendEphemeralSortLineItem(configuration: EphemeralSortConfiguration): EphemeralSortConfiguration {
    if (configuration.lineItems.some((lineItem) => lineItem.columnId === "")) {
        return configuration;
    }
    return {
        revisionId: generateUuid(),
        lineItems: [...configuration.lineItems, createEphemeralSortLineItem()],
    };
}

function toAppliedSortConfiguration(configuration: EphemeralSortConfiguration): SortConfiguration {
    return {
        revisionId: configuration.revisionId,
        lineItems: configuration.lineItems
            .filter((lineItem) => lineItem.columnId !== "")
            .map(({lineItemId: _lineItemId, ...lineItem}) => lineItem),
    };
}

export function SortComponent<
    T extends {
        id: Uuid;
    },
>({controller}: {controller: SortComponentController<T>}) {
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});

    const [ephemeralSortConfiguration, setEphemeralSortConfiguration] = useState<EphemeralSortConfiguration>(() => ({
        revisionId: generateUuid(),
        lineItems: [],
    }));

    const [opened, setOpened] = useState(false);
    const [addSortPickerOpen, setAddSortPickerOpen] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            setEphemeralSortConfiguration((prev) => {
                const oldIndex = prev.lineItems.findIndex((li) => li.lineItemId === active.id);
                const newIndex = prev.lineItems.findIndex((li) => li.lineItemId === over.id);

                const newLineItems = [...prev.lineItems];
                const [removed] = newLineItems.splice(oldIndex, 1);
                newLineItems.splice(newIndex, 0, removed);

                return {
                    revisionId: generateUuid(),
                    lineItems: newLineItems,
                };
            });
        }
    };

    const openPopup = () => {
        setEphemeralSortConfiguration(openEphemeralSortConfiguration(controller.sortConfiguration));
        setOpened(true);
    };
    const closePopup = () => {
        setOpened(false);
    };
    const closePopupAndDiscardChanges = () => {
        setOpened(false);
    };

    const columnNotCurrentlyUsedForSorting = controller.columns.lineItems.filter(
        (column) =>
            column.sortingCapability === true &&
            ephemeralSortConfiguration.lineItems.some((lineItem) => lineItem.columnId === column.id) === false,
    );

    const userAppliedSortRuleCount = resolveSortActiveRuleCount({
        sortConfiguration: controller.sortConfiguration,
        defaultSortConfiguration: controller.defaultSortConfiguration,
        showDefaultSortAsActive: controller.showDefaultSortAsActive,
    });

    const sortTriggerButton = (
        // Padding reserves room for the count badge inside the flex item box (avoids toolbar overflow clipping).
        <span className="relative inline-grid shrink-0 overflow-visible p-2">
            <AnankeIconButton
                type="button"
                appearance="default"
                icon={() => <ArrowDownUp className="w-4 h-4" />}
                label="Sort"
                isSelected={userAppliedSortRuleCount > 0}
                onClick={() => {
                    if (opened === false) {
                        openPopup();
                    } else {
                        closePopup();
                    }
                }}
            />
            {userAppliedSortRuleCount > 0 ? (
                <div className="absolute top-0 right-0 pointer-events-none">
                    <AnankeBadge appearance="primary">{userAppliedSortRuleCount}</AnankeBadge>
                </div>
            ) : null}
        </span>
    );

    if (isMobile === true) {
        return (
            <>
                {sortTriggerButton}

                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Sort"
                    zIndex={100}
                    fitContentOnOpen={true}
                    footer={
                        <div>
                            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                            <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                                <AnankeButton
                                    type="button"
                                    appearance="subtle"
                                    onClick={closePopupAndDiscardChanges}
                                >
                                    Cancel
                                </AnankeButton>
                                <AnankeButton
                                    type="button"
                                    appearance="primary"
                                    onClick={() => {
                                        controller.setSortConfiguration(toAppliedSortConfiguration(ephemeralSortConfiguration));
                                        closePopup();
                                    }}
                                    isDisabled={
                                        controller.sortConfiguration.revisionId ===
                                        ephemeralSortConfiguration.revisionId
                                    }
                                >
                                    Apply
                                </AnankeButton>
                            </div>
                        </div>
                    }
                >
                    {ephemeralSortConfiguration.lineItems.length > 0 && (
                        <div className="w-full px-ds-space-200 pb-ds-space-050 grid grid-cols-[1.5rem_minmax(0,1fr)_minmax(0,1fr)_2rem] gap-x-ds-space-075 items-center">
                            <div />
                            <span className="text-xs font-semibold" style={{color: token("color.text.subtle")}}>Column</span>
                            <span className="text-xs font-semibold" style={{color: token("color.text.subtle")}}>Order</span>
                            <div />
                        </div>
                    )}
                    {ephemeralSortConfiguration.lineItems.length > 0 && (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={ephemeralSortConfiguration.lineItems.map((lineItem) => lineItem.lineItemId)}
                                strategy={verticalListSortingStrategy}
                            >
                                <ItemBuilder
                                    items={ephemeralSortConfiguration.lineItems}
                                    itemBuilder={(sortConfigurationLineItem) => (
                                        <SortLineItemComponent<T>
                                            key={sortConfigurationLineItem.lineItemId}
                                            controller={controller}
                                            lineItem={sortConfigurationLineItem}
                                            ephemeralSortConfiguration={ephemeralSortConfiguration}
                                            setEphemeralSortConfiguration={setEphemeralSortConfiguration}
                                            isMobile={true}
                                        />
                                    )}
                                />
                            </SortableContext>
                        </DndContext>
                    )}

                    <button
                        type="button"
                        onClick={() => {
                            setEphemeralSortConfiguration((prev) => appendEphemeralSortLineItem(prev));
                        }}
                        className="w-full px-ds-space-100 py-ds-space-075 grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-ds-space-075 items-center text-left"
                    >
                        <span className="-ml-2 grid h-7 w-7 place-items-center text-ds-icon-subtle">
                            <AddIcon label="" />
                        </span>
                        <span style={{color: token("color.text")}}>Add sort</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setEphemeralSortConfiguration(() => ({
                                revisionId: generateUuid(),
                                lineItems: [],
                            }));
                        }}
                        className="w-full px-ds-space-100 py-ds-space-075 grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-ds-space-075 items-center text-left"
                    >
                        <span className="-ml-2 grid h-7 w-7 place-items-center text-ds-icon-subtle">
                            <CrossIcon label="" />
                        </span>
                        <span style={{color: token("color.text")}}>Clear sort</span>
                    </button>
                </BottomDrawer>

                {/* Column picker for "Add sort" */}
                <AddSortColumnPickerDrawer
                    open={addSortPickerOpen}
                    setOpen={setAddSortPickerOpen}
                    columns={columnNotCurrentlyUsedForSorting}
                    onSelect={(columnId) => {
                        setEphemeralSortConfiguration((prev) => ({
                            revisionId: generateUuid(),
                            lineItems: [
                                ...prev.lineItems,
                                createEphemeralSortLineItem(columnId, SortDirection.ascending),
                            ],
                        }));
                        setAddSortPickerOpen(false);
                    }}
                />
            </>
        );
    }

    return (
        <Popover
            position="bottom-start"
            width={380}
            opened={opened}
            onChange={(newValue) => {
                if (newValue === true) {
                    openPopup();
                } else {
                    closePopup();
                }
            }}
            classNames={{
                dropdown: "p-0!",
            }}
            shadow="lg"
        >
            <Popover.Target>{sortTriggerButton}</Popover.Target>

            <Popover.Dropdown>
                <div className="px-ds-space-200 py-ds-space-150 grid grid-cols-[minmax(0,1fr)_auto] items-center">
                    <span className="text-sm font-semibold" style={{color: token("color.text")}}>Sort</span>
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        icon={CrossIcon}
                        label="Close"
                        onClick={closePopupAndDiscardChanges}
                    />
                </div>

                {ephemeralSortConfiguration.lineItems.length > 0 && (
                    <>
                        <div className="w-full px-ds-space-200 pb-ds-space-050 grid grid-cols-[1.5rem_minmax(0,1fr)_minmax(0,1fr)_2rem] gap-x-ds-space-075 items-center">
                            <div />
                            <span className="text-xs font-semibold" style={{color: token("color.text.subtle")}}>Column</span>
                            <span className="text-xs font-semibold" style={{color: token("color.text.subtle")}}>Order</span>
                            <div />
                        </div>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={ephemeralSortConfiguration.lineItems.map((lineItem) => lineItem.lineItemId)}
                                strategy={verticalListSortingStrategy}
                            >
                                <ItemBuilder
                                    items={ephemeralSortConfiguration.lineItems}
                                    itemBuilder={(sortConfigurationLineItem) => (
                                        <SortLineItemComponent<T>
                                            key={sortConfigurationLineItem.lineItemId}
                                            controller={controller}
                                            lineItem={sortConfigurationLineItem}
                                            ephemeralSortConfiguration={ephemeralSortConfiguration}
                                            setEphemeralSortConfiguration={setEphemeralSortConfiguration}
                                            isMobile={false}
                                        />
                                    )}
                                />
                            </SortableContext>
                        </DndContext>
                    </>
                )}

                <div className="py-ds-space-050">
                    <AnankeDropdownItem
                        onClick={() => {
                            setEphemeralSortConfiguration((prev) => appendEphemeralSortLineItem(prev));
                        }}
                    >
                        <div className="w-full grid grid-cols-[auto_minmax(0,1fr)] gap-x-ds-space-050 items-center text-sm">
                            <div className="grid h-5 w-5 place-items-center text-ds-icon-subtle"><AddIcon label="" /></div>
                            <div>Add sort</div>
                        </div>
                    </AnankeDropdownItem>

                    <AnankeDropdownItem
                        onClick={() => {
                            setEphemeralSortConfiguration(() => ({
                                revisionId: generateUuid(),
                                lineItems: [],
                            }));
                        }}
                    >
                        <div className="w-full grid grid-cols-[auto_minmax(0,1fr)] gap-x-ds-space-050 items-center text-sm">
                            <div className="grid h-5 w-5 place-items-center text-ds-icon-subtle"><CrossIcon label="" /></div>
                            <div>Clear sort</div>
                        </div>
                    </AnankeDropdownItem>
                </div>

                <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />

                <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                    <AnankeButton
                        type="button"
                        appearance="subtle"
                        onClick={closePopupAndDiscardChanges}
                    >
                        Cancel
                    </AnankeButton>

                    <AnankeButton
                        type="button"
                        appearance="primary"
                        onClick={() => {
                            controller.setSortConfiguration(toAppliedSortConfiguration(ephemeralSortConfiguration));
                            closePopup();
                        }}
                        isDisabled={controller.sortConfiguration.revisionId === ephemeralSortConfiguration.revisionId}
                    >
                        Apply
                    </AnankeButton>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}

function NoSortLineItemComponent({isMobile}: {isMobile: boolean}) {
    return (
        <div
            className={
                isMobile
                    ? "w-full px-ds-space-100 py-ds-space-150 grid grid-cols-1 items-center"
                    : "w-full h-[2.5rem] px-ds-space-200 py-ds-space-100 grid grid-cols-1 items-center"
            }
        >
            {isMobile ? "No Sort Applied" : "No sorting applied"}
        </div>
    );
}

function AddSortColumnPickerDrawer({
    open,
    setOpen,
    columns,
    onSelect,
}: {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    columns: Array<OttopilotColumn<{id: Uuid}>>;
    onSelect: (columnId: Uuid) => void;
}) {
    const [searchValue, setSearchValue] = useState("");

    const filteredColumns = columns.filter((column) => {
        if (searchValue.trim() === "") {
            return true;
        }
        return column.displayName.toLowerCase().includes(searchValue.toLowerCase().trim());
    });

    return (
        <BottomDrawer
            open={open}
            setOpen={setOpen}
            title="Column"
            zIndex={200}
            fitContentOnOpen={true}
        >
            <div className="px-ds-space-075 pt-ds-space-050 pb-ds-space-100">
                <AnankeTextField
                    id="add-sort-column-search"
                    value={searchValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchValue(e.currentTarget.value);
                    }}
                    placeholder="Search"
                    elemBeforeInput={
                        <div className="pl-ds-space-100 grid place-items-center">
                            <SearchIcon label="" />
                        </div>
                    }
                />
            </div>

            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />

            <ItemBuilder
                items={filteredColumns}
                itemBuilder={(column) => (
                    <AnankeDropdownItemCheckbox
                        key={column.id}
                        id={column.id}
                        isSelected={false}
                        onClick={() => {
                            onSelect(column.id);
                        }}
                    >
                        {column.displayName}
                    </AnankeDropdownItemCheckbox>
                )}
            />
        </BottomDrawer>
    );
}
// Note: AddSortColumnPickerDrawer uses immediate selection (no footer) since clicking a column auto-applies

function SortLineItemComponent<
    T extends {
        id: Uuid;
    },
>({
    controller,
    lineItem,
    ephemeralSortConfiguration,
    setEphemeralSortConfiguration,
    isMobile,
}: {
    controller: SortComponentController<T>;
    lineItem: EphemeralSortLineItem;
    ephemeralSortConfiguration: EphemeralSortConfiguration;
    setEphemeralSortConfiguration: Dispatch<SetStateAction<EphemeralSortConfiguration>>;
    isMobile: boolean;
}) {
    const [columnPickerOpen, setColumnPickerOpen] = useState(false);
    const [orderPickerOpen, setOrderPickerOpen] = useState(false);

    // Columns available for this line item: all sortable columns except those used by OTHER line items
    const otherLineItemColumnIds = new Set(
        ephemeralSortConfiguration.lineItems
            .filter((li) => li.lineItemId !== lineItem.lineItemId && li.columnId !== "")
            .map((li) => li.columnId),
    );
    const availableColumnsForLineItem = controller.columns.lineItems.filter(
        (column) => column.sortingCapability === true && otherLineItemColumnIds.has(column.id) === false,
    );

    const currentColumnName =
        lineItem.columnId === ""
            ? "Select"
            : (controller.columns.lineItems.find((column) => column.id === lineItem.columnId)?.displayName ??
              lineItem.columnId);

    const currentDirectionLabel =
        lineItem.direction === ("" as SortDirection)
            ? "Select"
            : lineItem.direction === SortDirection.ascending
              ? "Ascending"
              : lineItem.direction === SortDirection.descending
                ? "Descending"
                : "?";

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id: lineItem.lineItemId});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundColor: token("elevation.surface"),
    };

    if (isMobile === true) {
        return (
            <>
                <div
                    ref={setNodeRef}
                    style={style}
                    className="w-full px-ds-space-200 py-ds-space-100 grid grid-cols-[1.5rem_minmax(0,1fr)_minmax(0,1fr)_2rem] gap-x-ds-space-075 items-center z-10 relative"
                >
                    <div
                        className="-ml-2 grid h-7 w-7 place-items-center text-ds-icon-subtle touch-none"
                        {...attributes}
                        {...listeners}
                    >
                        <DragHandleVerticalIcon label="" />
                    </div>

                    <button
                            type="button"
                            className="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-1 rounded border px-2 py-1.5 text-sm text-left"
                            style={{
                                borderColor: token("color.border"),
                                color: token("color.text"),
                                backgroundColor: token("elevation.surface"),
                            }}
                            onClick={() => {
                                setColumnPickerOpen(true);
                            }}
                        >
                            <span className="truncate">{currentColumnName}</span>
                            <ChevronDownIcon
                                label=""
                                size="small"
                            />
                        </button>

                        <button
                            type="button"
                            className="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-1 rounded border px-2 py-1.5 text-sm text-left"
                            style={{
                                borderColor: token("color.border"),
                                color: token("color.text"),
                                backgroundColor: token("elevation.surface"),
                            }}
                            onClick={() => {
                                setOrderPickerOpen(true);
                            }}
                        >
                            <span className="truncate">{currentDirectionLabel}</span>
                            <ChevronDownIcon
                                label=""
                                size="small"
                            />
                        </button>

                    <button
                        type="button"
                        className="grid h-7 w-7 place-items-center rounded text-ds-icon-subtle hover:text-ds-icon"
                        aria-label="Remove sort condition"
                        onClick={() => {
                            setEphemeralSortConfiguration((prev) => ({
                                revisionId: generateUuid(),
                                lineItems: prev.lineItems.filter(
                                    (lineItem_) => lineItem.lineItemId !== lineItem_.lineItemId,
                                ),
                            }));
                        }}
                    >
                        <CrossIcon label="" />
                    </button>
                </div>

                {/* Column picker drawer */}
                <SortColumnPickerDrawer
                    open={columnPickerOpen}
                    setOpen={setColumnPickerOpen}
                    columns={availableColumnsForLineItem}
                    selectedColumnId={lineItem.columnId}
                    onApply={(newColumnId) => {
                        setEphemeralSortConfiguration((prev) => ({
                            revisionId: generateUuid(),
                            lineItems: prev.lineItems.map((lineItem_) =>
                                lineItem_.lineItemId === lineItem.lineItemId
                                    ? {...lineItem_, columnId: newColumnId}
                                    : lineItem_,
                            ),
                        }));
                        setColumnPickerOpen(false);
                    }}
                />

                {/* Order picker drawer */}
                <SortOrderPickerDrawer
                    open={orderPickerOpen}
                    setOpen={setOrderPickerOpen}
                    selectedDirection={lineItem.direction}
                    onApply={(newDirection) => {
                        setEphemeralSortConfiguration((prev) => ({
                            revisionId: generateUuid(),
                            lineItems: prev.lineItems.map((lineItem_) =>
                                lineItem_.lineItemId === lineItem.lineItemId
                                    ? {...lineItem_, direction: newDirection}
                                    : lineItem_,
                            ),
                        }));
                        setOrderPickerOpen(false);
                    }}
                />
            </>
        );
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="w-full px-ds-space-200 py-ds-space-075 grid grid-cols-[1.5rem_minmax(0,1fr)_minmax(0,1fr)_2rem] gap-x-ds-space-075 items-center z-10 relative"
            key={lineItem.lineItemId}
        >
            <div
                className="-ml-2 grid h-7 w-7 place-items-center text-ds-icon-subtle cursor-grab touch-none"
                {...attributes}
                {...listeners}
            >
                <DragHandleVerticalIcon label="" />
            </div>

            <AnankeDropdownMenu
                trigger={currentColumnName}
                shouldRenderToParent={true}
            >
                <AnankeDropdownItemGroup>
                    <ItemBuilder
                        items={availableColumnsForLineItem}
                        itemBuilder={(column) => (
                            <AnankeDropdownItem
                                onClick={() => {
                                    setEphemeralSortConfiguration((prev) => ({
                                        revisionId: generateUuid(),
                                        lineItems: prev.lineItems.map((lineItem_) =>
                                            lineItem_.lineItemId === lineItem.lineItemId
                                                ? {
                                                      ...lineItem_,
                                                      columnId: column.id,
                                                      direction: lineItem_.direction === "" as SortDirection ? SortDirection.ascending : lineItem_.direction,
                                                  }
                                                : lineItem_,
                                        ),
                                    }));
                                }}
                            >
                                {column.displayName}
                            </AnankeDropdownItem>
                        )}
                    />
                </AnankeDropdownItemGroup>
            </AnankeDropdownMenu>

            <AnankeDropdownMenu
                trigger={currentDirectionLabel}
                shouldRenderToParent={true}
            >
                <AnankeDropdownItemGroup>
                    <AnankeDropdownItem
                        onClick={() => {
                            setEphemeralSortConfiguration((prev) => ({
                                revisionId: generateUuid(),
                                lineItems: prev.lineItems.map((lineItem_) =>
                                    lineItem_.lineItemId === lineItem.lineItemId
                                        ? {
                                              ...lineItem_,
                                              direction: SortDirection.ascending,
                                          }
                                        : lineItem_,
                                ),
                            }));
                        }}
                    >
                        Ascending
                    </AnankeDropdownItem>

                    <AnankeDropdownItem
                        onClick={() => {
                            setEphemeralSortConfiguration((prev) => ({
                                revisionId: generateUuid(),
                                lineItems: prev.lineItems.map((lineItem_) =>
                                    lineItem_.lineItemId === lineItem.lineItemId
                                        ? {
                                              ...lineItem_,
                                              direction: SortDirection.descending,
                                          }
                                        : lineItem_,
                                ),
                            }));
                        }}
                    >
                        Descending
                    </AnankeDropdownItem>
                </AnankeDropdownItemGroup>
            </AnankeDropdownMenu>

            <AnankeIconButton
                type="button"
                appearance="subtle"
                icon={CrossIcon}
                label=""
                onClick={() => {
                    setEphemeralSortConfiguration((prev) => ({
                        revisionId: generateUuid(),
                        lineItems: prev.lineItems.filter((lineItem_) => lineItem.lineItemId !== lineItem_.lineItemId),
                    }));
                }}
            />
        </div>
    );
}

function SortColumnPickerDrawer({
    open,
    setOpen,
    columns,
    selectedColumnId,
    onApply,
}: {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    columns: Array<OttopilotColumn<{id: Uuid}>>;
    selectedColumnId: Uuid;
    onApply: (columnId: Uuid) => void;
}) {
    const [searchValue, setSearchValue] = useState("");
    const [ephemeralColumnId, setEphemeralColumnId] = useState<Uuid>(selectedColumnId);

    const filteredColumns = columns.filter((column) => {
        if (searchValue.trim() === "") {
            return true;
        }
        return column.displayName.toLowerCase().includes(searchValue.toLowerCase().trim());
    });

    const selectedCount = columns.some((c) => c.id === ephemeralColumnId) ? 1 : 0;

    return (
        <BottomDrawer
            open={open}
            setOpen={setOpen}
            title="Column"
            zIndex={200}
            fitContentOnOpen={true}
            footer={
                <div>
                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                    <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                        <AnankeButton
                            type="button"
                            appearance="subtle"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            Cancel
                        </AnankeButton>
                        <AnankeButton
                            type="button"
                            appearance="primary"
                            onClick={() => {
                                onApply(ephemeralColumnId);
                            }}
                        >
                            Apply
                        </AnankeButton>
                    </div>
                </div>
            }
        >
            <div className="px-ds-space-075 pt-ds-space-050 pb-ds-space-100">
                <AnankeTextField
                    id="sort-column-search"
                    value={searchValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchValue(e.currentTarget.value);
                    }}
                    placeholder="Search"
                    elemBeforeInput={
                        <div className="pl-ds-space-100 grid place-items-center">
                            <SearchIcon label="" />
                        </div>
                    }
                />
            </div>

            <div
                className="w-full px-ds-space-200 py-ds-space-075 text-sm"
                style={{color: token("color.text.subtle")}}
            >
                {selectedCount}/{columns.length} Selected
            </div>

            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />

            <ItemBuilder
                items={filteredColumns}
                itemBuilder={(column) => (
                    <AnankeDropdownItemCheckbox
                        key={column.id}
                        id={column.id}
                        isSelected={ephemeralColumnId === column.id}
                        onClick={() => {
                            setEphemeralColumnId(column.id);
                        }}
                    >
                        {column.displayName}
                    </AnankeDropdownItemCheckbox>
                )}
            />
        </BottomDrawer>
    );
}

function SortOrderPickerDrawer({
    open,
    setOpen,
    selectedDirection,
    onApply,
}: {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    selectedDirection: SortDirection;
    onApply: (direction: SortDirection) => void;
}) {
    const [ephemeralDirection, setEphemeralDirection] = useState<SortDirection>(selectedDirection);

    return (
        <BottomDrawer
            open={open}
            setOpen={setOpen}
            title="Order"
            zIndex={200}
            fitContentOnOpen={true}
            footer={
                <div>
                    <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
                    <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                        <AnankeButton
                            type="button"
                            appearance="subtle"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            Cancel
                        </AnankeButton>
                        <AnankeButton
                            type="button"
                            appearance="primary"
                            onClick={() => {
                                onApply(ephemeralDirection);
                            }}
                        >
                            Apply
                        </AnankeButton>
                    </div>
                </div>
            }
        >
            <AnankeDropdownItemCheckbox
                id="sort-direction-ascending"
                isSelected={ephemeralDirection === SortDirection.ascending}
                onClick={() => {
                    setEphemeralDirection(SortDirection.ascending);
                }}
            >
                Ascending
            </AnankeDropdownItemCheckbox>

            <AnankeDropdownItemCheckbox
                id="sort-direction-descending"
                isSelected={ephemeralDirection === SortDirection.descending}
                onClick={() => {
                    setEphemeralDirection(SortDirection.descending);
                }}
            >
                Descending
            </AnankeDropdownItemCheckbox>
        </BottomDrawer>
    );
}
